import { format } from 'date-fns';
import c from 'classnames';

import { parseDate } from 'helpers';
import useSubscriptions from 'services/general/subscriptionsList';
import { useTinyAccountForHome } from 'hooks/useTinyAccountForHome';
import useCheckoutKit from 'hooks/useCheckoutKit';
import {
  Subscription,
  SubscriptionStatus,
  SubscriptionType,
} from 'types/Subscription';

import {
  ButtonColors,
  ButtonSizes,
  ButtonVariants,
  Card,
  LinkButton,
  TextLink,
} from 'components';
import { Icon, Typography } from '@repo/ui';
import { FadedCard } from '../FadedCard/FadedCard';

import styles from './MembershipSection.module.scss';

export const MembershipSectionCard = ({
  subscription,
  tinyAccountId,
}: {
  subscription: Subscription;
  tinyAccountId: string | null;
}) => {
  const { latestKit } = useTinyAccountForHome(tinyAccountId);
  const { price, checkoutUrl } = useCheckoutKit({
    kitType: latestKit?.sampling_type,
  });
  const checkoutUrlWithUtm = `${checkoutUrl}&utm_source=tinyhealth&utm_medium=webapp&utm_campaign=benefits`;
  const hasUnusedKits =
    !!subscription.kits_count_total &&
    subscription.kits_count_used !== subscription.kits_count_total;

  if (!subscription || subscription.status !== SubscriptionStatus.ACTIVE)
    return <></>;

  return (
    <FadedCard>
      <div className='flex flex-column gap-3'>
        <div className={styles.cardHeader}>
          <Typography variant='heading-l'>
            {subscription.shopify_product_name ?? 'Your Tiny+ Membership'}
          </Typography>
          <div className='flex align-items-center gap-1 light-grey-text'>
            <Icon name='award' size='s' />
            <Typography variant='label' serif>
              {subscription.date_next_charge_scheduled
                ? `Renews ${format(
                    parseDate(subscription.date_next_charge_scheduled),
                    'MM/dd/yyyy',
                  )}`
                : 'Annual'}
            </Typography>
          </div>
        </div>

        <div className={styles.grid}>
          <Card variant='wrapper'>
            <div className={styles.cardContent}>
              <Typography
                variant='heading-3xl'
                serif
                className={styles.quantity}
              >
                <span className={styles.quantityDark}>
                  {subscription.consult_credits_count_used ?? 0}
                </span>
                {` / ${subscription.consult_credits_count_total ?? 0}`}
              </Typography>
              <div className={c(styles.pill, styles.blue)}>
                <Typography variant='label' className='my-auto'>
                  coaching sessions
                  <Typography variant='label' className='hide-on-mobile'>
                    {` used`}
                  </Typography>
                </Typography>
              </div>

              <div className='flex align-items-center justify-content-between gap-2 mt-1'>
                <Typography variant='label' className='dark-grey'>
                  $39 / additional call
                </Typography>
                {subscription.subscription_type ===
                  SubscriptionType.TINY_PLUS && (
                  <div className={styles.desktopActions}>
                    <TextLink
                      label='Book a call'
                      iconNameRight='chevronForward'
                      to='/consults'
                    />
                  </div>
                )}
              </div>
            </div>
          </Card>
          <Card
            variant='wrapper'
            clickable={{
              text: hasUnusedKits ? 'Activate a kit' : 'Buy a kit',
              href: hasUnusedKits ? '/activate' : checkoutUrlWithUtm,
              isExternal: !hasUnusedKits,
              variety: 'Card',
            }}
          >
            <div className={styles.cardContent}>
              <Typography
                variant='heading-3xl'
                serif
                className={styles.quantity}
              >
                <span className={styles.quantityDark}>
                  {subscription.kits_count_used ?? 0}
                </span>
                {` / ${subscription.kits_count_total ?? 0}`}
              </Typography>
              <div className={c(styles.pill, styles.purple)}>
                <Typography variant='label'>
                  test kits
                  <Typography variant='label' className='hide-on-mobile'>
                    {` used`}
                  </Typography>
                </Typography>
              </div>
              <div className='flex align-items-center justify-content-between gap-2 mt-1'>
                <Typography variant='label' className='dark-grey'>
                  {`$${price} / additional kit`}
                </Typography>
                <div className={styles.desktopActions}>
                  <TextLink
                    label={hasUnusedKits ? 'Activate a kit' : 'Buy a kit'}
                    iconNameRight='chevronForward'
                    contentOnly
                  />
                </div>
              </div>
            </div>
          </Card>
          <Card
            variant='wrapper'
            clickable={{
              text: 'Chat with us',
              href: '/help',
              isExternal: false,
              variety: 'Card',
            }}
            className={c(styles.cardContent, styles.big)}
          >
            <Typography variant='heading-xl' serif>
              Chat with us
            </Typography>
            <div className={c(styles.pill, styles.green, 'mt-1 mb-1')}>
              <Typography variant='label'>SMS and Email support</Typography>
            </div>
            <div
              className={c(
                styles.desktopActions,
                'mt-1 justify-content-center',
              )}
            >
              <TextLink
                label='Message us'
                iconNameRight='chevronForward'
                contentOnly
              />
            </div>
          </Card>
        </div>

        <LinkButton
          label='Buy additional kits'
          href={checkoutUrlWithUtm}
          external
          variant={ButtonVariants.SOLID}
          size={ButtonSizes.M}
          color={ButtonColors.DARK_PURPLE}
          width={'full'}
        />
      </div>
    </FadedCard>
  );
};

export const MembershipSection = ({
  tinyAccountId,
}: {
  tinyAccountId: string | null;
}) => {
  const { data: subscriptionsList } = useSubscriptions();

  if (!subscriptionsList?.length) return <></>;
  return (
    <>
      {subscriptionsList.map(subscription => (
        <MembershipSectionCard
          tinyAccountId={tinyAccountId}
          subscription={subscription}
          key={subscription.id}
        />
      ))}
    </>
  );
};
